import React, { useState, useEffect } from 'react';
import { useOrgProject } from '../contexts/OrgProjectContext';
import { apiFetch } from '../apiClient';

const API       = process.env.REACT_APP_API_URL || 'http://localhost:5001';
const DEFAULT_THREAD_ID = process.env.REACT_APP_THREAD_ID || 'real-sdlc-001';

function getThreadId(orgId, projectId) {
  if (orgId && projectId) return `pipeline-${orgId.slice(0, 8)}-${projectId.slice(0, 8)}`;
  if (orgId) return `pipeline-${orgId.slice(0, 8)}`;
  return DEFAULT_THREAD_ID;
}

export default function AppPortfolio() {
  const { orgId, projectId } = useOrgProject();
  const threadId = getThreadId(orgId, projectId);
  const [apps, setApps]         = useState([]);
  const [analyses, setAnalyses] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [collapsed, setCollapsed] = useState(false);

  // Poll pipeline state to get live scanned_apps + app_analyses
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await apiFetch(`/api/state?thread_id=${threadId}`);
        if (!r.ok || cancelled) return;
        const data = await r.json();
        if (data.scanned_apps?.length > 0) setApps(data.scanned_apps);
        if (data.app_analyses?.length > 0)  setAnalyses(data.app_analyses);
      } catch (_) {}
    };
    load();
    const interval = setInterval(load, 8000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [threadId]);

  const toggle = (name) => setExpanded(prev => prev === name ? null : name);

  if (!apps.length) return null;

  return (
    <div style={{ padding: '16px 20px 0' }}>
      <div style={{
        background: '#fff', border: '1px solid var(--border)',
        borderRadius: 10, overflow: 'hidden', boxShadow: 'var(--shadow)',
      }}>
        {/* Collapsible header */}
        <div
          onClick={() => setCollapsed(c => !c)}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '9px 16px', cursor: 'pointer',
            background: 'var(--bg2)', borderBottom: collapsed ? 'none' : '1px solid var(--border)',
            userSelect: 'none',
          }}
        >
          <span style={{
            fontSize: 12, color: 'var(--txt2)',
            transform: collapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s', display: 'inline-block',
          }}>▼</span>
          <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--txt2)',
            textTransform: 'uppercase', letterSpacing: '0.5px', flex: 1 }}>
            Applications
          </span>
          <span style={{ fontSize: 11, color: 'var(--txt2)', fontWeight: 400 }}>
            {apps.length} app{apps.length !== 1 ? 's' : ''}
            {analyses.length > 0 && ` · ${analyses.filter(a => a.llm_recommendation).length} scored`}
          </span>
        </div>

        {!collapsed && (
          <>
            {/* Table column headers */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '32px 1fr 180px 120px 90px 28px',
              padding: '7px 16px',
              background: 'var(--bg2)', borderBottom: '1px solid var(--border)',
              fontSize: 10, fontWeight: 700, color: 'var(--txt2)',
              textTransform: 'uppercase', letterSpacing: '0.5px',
            }}>
              <div /><div>Application</div><div>Tech Stack</div>
              <div>Issues Found</div><div>Recommendation</div><div />
            </div>

            {apps.map((app, idx) => {
              const analysis = analyses.find(a => a.name === app.name) || {};
              const rec       = analysis.llm_recommendation || null;
              const isOpen    = expanded === app.name;
              const isLast    = idx === apps.length - 1;

              const recStyle = {
                Refactor: { bg: '#FFF8C5', color: '#9A6700', border: 'rgba(154,103,0,0.3)' },
                New:      { bg: '#DAFBE1', color: '#1A7F37', border: 'rgba(26,127,55,0.3)' },
                Retire:   { bg: '#FFEBE9', color: '#CF222E', border: 'rgba(207,34,46,0.3)' },
                Replace:  { bg: '#FBEFFF', color: '#6639BA', border: 'rgba(102,57,186,0.3)' },
              }[rec] || { bg: '#EFF2F5', color: '#57606A', border: 'rgba(87,96,106,0.2)' };

              const targetStack = analysis.target_stack;
              const langColor = {
                Python: '#0969DA', Java: '#9A6700', 'C#': '#6639BA',
                JavaScript: '#1A7F37', TypeScript: '#1A7F37',
              }[app.language] || '#57606A';

              return (
                <div key={app.name} style={{ borderBottom: isLast ? 'none' : '1px solid var(--border)' }}>
                  <div
                    onClick={() => toggle(app.name)}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '32px 1fr 180px 120px 90px 28px',
                      padding: '11px 16px', alignItems: 'center',
                      cursor: 'pointer',
                      background: isOpen ? '#F6F8FA' : '#fff',
                      transition: 'background 0.15s',
                      borderLeft: `4px solid ${langColor}`,
                    }}
                    onMouseEnter={e => { if (!isOpen) e.currentTarget.style.background = '#F6F8FA'; }}
                    onMouseLeave={e => { if (!isOpen) e.currentTarget.style.background = '#fff'; }}
                  >
                    <span style={{ fontSize: 16 }}>
                      {{ Python: '🐍', Java: '☕', 'C#': '🔷', JavaScript: '🟨', TypeScript: '🟦' }[app.language] || '📦'}
                    </span>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--txt)' }}>
                        {app.name.replace('legacy-', '')}
                        <span style={{ fontWeight: 400, fontSize: 11, color: 'var(--txt2)', marginLeft: 6, fontFamily: 'monospace' }}>
                          {app.name}
                        </span>
                      </div>
                      {analysis.llm_reasoning && (
                        <div style={{ fontSize: 11, color: 'var(--txt2)', marginTop: 2, lineHeight: 1.4 }}>
                          {analysis.llm_reasoning.slice(0, 80)}…
                        </div>
                      )}
                    </div>
                    <div style={{ fontSize: 11, color: langColor, fontFamily: 'monospace', lineHeight: 1.5 }}>
                      {app.language} · {app.framework}
                      {targetStack?.language && targetStack.language !== app.language && (
                        <div style={{ color: '#1A7F37', marginTop: 2 }}>
                          → {targetStack.language}/{targetStack.framework}
                        </div>
                      )}
                    </div>
                    <div>
                      <span style={{
                        background: '#FFEBE9', color: '#CF222E',
                        border: '1px solid rgba(207,34,46,0.25)',
                        borderRadius: 10, padding: '2px 9px',
                        fontSize: 11, fontWeight: 700,
                      }}>
                        ✗ {app.issues?.length || 0} issues
                      </span>
                    </div>
                    <div>
                      {rec ? (
                        <span style={{
                          fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 10,
                          background: recStyle.bg, color: recStyle.color,
                          border: `1px solid ${recStyle.border}`,
                        }}>
                          {rec}
                        </span>
                      ) : (
                        <span style={{ fontSize: 11, color: 'var(--txt2)', fontStyle: 'italic' }}>pending</span>
                      )}
                    </div>
                    <div style={{
                      fontSize: 13, color: 'var(--txt2)',
                      transform: isOpen ? 'rotate(90deg)' : 'none',
                      transition: 'transform 0.2s', textAlign: 'center',
                    }}>›</div>
                  </div>

                  {isOpen && (
                    <div style={{
                      padding: '16px 20px 18px 52px',
                      background: '#F6F8FA', borderTop: '1px solid var(--border)',
                      display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 20,
                    }}>
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--txt2)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>Discovery</div>
                        <div style={{ fontSize: 12, color: 'var(--txt)', lineHeight: 1.6 }}>
                          <strong>{app.language}</strong> · {app.framework}<br />
                          {app.loc?.toLocaleString()} lines of code · {app.files} files
                        </div>
                        {targetStack?.language && (
                          <div style={{ marginTop: 10, padding: '8px 10px', background: '#DAFBE1', borderRadius: 6, fontSize: 11, color: '#1A7F37' }}>
                            <strong>Target stack:</strong> {targetStack.language}/{targetStack.framework}<br />
                            {targetStack.reasoning && <span style={{ opacity: 0.8 }}>{targetStack.reasoning}</span>}
                          </div>
                        )}
                      </div>
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--txt2)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>Issues Found</div>
                        {app.issues?.length > 0 ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {app.issues.map((issue, i) => (
                              <div key={i} style={{ display: 'flex', gap: 7, fontSize: 12, color: 'var(--txt)', alignItems: 'flex-start' }}>
                                <span style={{ color: '#CF222E', flexShrink: 0, fontWeight: 700 }}>✗</span>
                                {issue}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div style={{ fontSize: 12, color: 'var(--txt2)', fontStyle: 'italic' }}>No issues detected</div>
                        )}
                      </div>
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--txt2)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>AI Assessment</div>
                        {rec ? (
                          <div style={{ background: recStyle.bg, border: `1px solid ${recStyle.border}`, borderRadius: 8, padding: '12px 14px' }}>
                            <div style={{ fontSize: 16, fontWeight: 800, color: recStyle.color, marginBottom: 6 }}>
                              {rec}
                              {analysis.llm_score > 0 && (
                                <span style={{ fontSize: 12, fontWeight: 400, marginLeft: 8, opacity: 0.7 }}>
                                  score {analysis.llm_score?.toFixed(1)}/10
                                </span>
                              )}
                            </div>
                            <div style={{ fontSize: 12, color: 'var(--txt)', lineHeight: 1.6 }}>{analysis.llm_reasoning}</div>
                          </div>
                        ) : (
                          <div style={{ fontSize: 12, color: 'var(--txt2)', fontStyle: 'italic' }}>Run the pipeline to get AI assessment.</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
}
